import React, { FunctionComponent, useEffect, useState } from "react";
import BrandKindSelect from "@Components/common/singleBrandKindSelect/brandKindSelect";
import Article, { IListLink } from "@Components/article/listLink";
import throttle from "lodash/throttle";
import { get } from "@Lib/request";
import { article } from "@Api";
import { fetchV3Articles, fetchNewCarBrands, fetchNewCarKinds } from "../../services/article_v2";
import { Spin } from "antd";
import { useSetState } from "ahooks-v2";
import cx from "classnames";
import "@Styles/global.less";
import ga from "@Lib/ga";

import styles from "./articleIndex.module.less";

type NavType = {
  name: string;
  contentType1: undefined | string;
  contentType2?: undefined | string;
  id?: number;
  isNative?: boolean;
  hrefPrefix?: string;
};

interface IProps {
  nav: Array<NavType>;
  fetchParams: Partial<IParams>;
}

interface IData {
  dataSource: IListLink[];
  nextPage: string;
}

interface IDataSource {
  index: any[];
  brand: any[];
  kindNum: number;
}

interface IParams {
  brandId: undefined | number;
  kindId: undefined | number | "";
  contentType1: undefined | string;
  contentType2: undefined | string;
  filterNewest: undefined | number;
  type: undefined | string;
  limit: number;
}

// const {
//   fetchV3Articles,
//   fetchNewCarBrands,
//   fetchNewCarKinds
// } = article;

const ArticleList: FunctionComponent<IProps> = ({ nav, fetchParams }) => {
  const [active, setActive] = useState<number>(0);
  const [data, setData] = useSetState<IData>({
    dataSource: [],
    nextPage: "",
  });
  const [newCarKind, setNewCarKind] = useState<any[]>([]);
  const [newCarDataSource, setNewCarDataSource] = useState<IDataSource>({
    index: [],
    brand: [],
    kindNum: 0,
  });
  const [loadingMore, setLoading] = useState<boolean>(false);
  const [isShowLoadMore, setIsShowLoadMore] = useState<boolean>(false);
  const [isShowClear, setShowClear] = useState<boolean>(false);
  const [params, setParams] = useSetState<IParams>({
    kindId: undefined,
    brandId: undefined,
    contentType1: undefined,
    contentType2: undefined,
    filterNewest: undefined,
    type: undefined,
    limit: 9,
  });

  const fetch = async (params: Partial<IParams>) => {
    if (params.hasOwnProperty("kindId") && params.kindId !== "" && params.kindId !== undefined) {
      delete params.filterNewest;
    }
    const res = await fetchV3Articles({ ...params });

    if (res && res.status === 12000) {
      setData({
        dataSource: res.data.list,
        nextPage: res.data.paging,
      });
      setIsShowLoadMore(!!res.data.list.length && !!res.data.paging);
    }
    setLoading(false);
  };

  const setTab = (data: NavType, inx: number) => {
    setLoading(true);
    const { contentType1, contentType2 } = data;

    setActive(inx);
    fetch({ ...params, contentType1, contentType2 });
  };

  const fetchSelect = async () => {
    const res = await fetchNewCarBrands();

    if (res && res.status === 12000) {
      setNewCarDataSource(res.data);
    }
  };

  const handleNewCarBrandSelect = async (brand: number) => {
    setNewCarKind([]);
    setParams({ brandId: brand });
    const res = await fetchNewCarKinds({ b: brand });
    if (res && res.status === 12000) {
      setNewCarKind(res.data.data);
      setShowClear(true);
    }
  };

  const loadMore = async () => {
    if (!data.nextPage) return;

    setLoading(true);
    const res = await get<any>(data.nextPage);
    setLoading(false);
    if (res && res.status === 12000) {
      setData((prev) => ({
        dataSource: prev.dataSource.concat(res.data.list),
        nextPage: res.data.paging,
      }));
      setIsShowLoadMore(!!res.data.list.length && !!res.data.paging);
    }
  };

  const handleScroll = throttle(() => {
    if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 380) {
      loadMore();
    }
  }, 500);

  const onSubmit = (data: any) => {
    if (data === "all") {
      setLoading(true);
      setParams((prev) => ({
        ...prev,
        kindId: "",
      }));
      fetch({ ...params, kindId: "" });
      return;
    }

    const { brandId, kindId } = data;
    setLoading(true);
    setParams({
      brandId,
      kindId,
    });
    fetch({ ...params, brandId, kindId });
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [data]);

  useEffect(() => {
    if (fetchParams) {
      fetch(fetchParams);
      setParams(fetchParams);
      return;
    }
    fetch(params);
  }, [fetchParams]);

  useEffect(() => {
    fetchSelect();
  }, []);

  return (
    <div className={styles["all-article"]}>
      <div className={styles["all-article-bar"]}>
        <div className={cx(styles["article-nav"], { [styles["small-nav"]]: nav.length > 5 })}>
          {nav && nav.length !== 0 ? (
            nav.map((item, inx) =>
              !item?.isNative ? (
                <div
                  className={cx(styles["nav-item"], { [styles["active"]]: active === inx })}
                  key={inx}
                  onClick={() => setTab(item, inx)}
                >
                  {item.name}
                </div>
              ) : (
                <a
                  className={cx(styles["nav-item"], { [styles["active"]]: active === inx })}
                  href={`${item.hrefPrefix}?t=${item.id}`}
                >
                  {" "}
                  {item.name}{" "}
                </a>
              ),
            )
          ) : (
            <h3>全部文章</h3>
          )}
        </div>
        <div className={styles["brand-select"]}>
          <BrandKindSelect
            field={"newcar"}
            selectLabel={"選擇廠牌車款"}
            dataSource={newCarDataSource}
            kindData={newCarKind}
            onBrandSelect={handleNewCarBrandSelect}
            onClear={() => {
              setParams({
                kindId: undefined,
                brandId: undefined,
              });
              setShowClear(false);
              setLoading(true);
              fetch({ ...params, kindId: undefined, brandId: undefined });
            }}
            isShowClear={isShowClear}
            onSubmit={onSubmit}
          />
        </div>
      </div>
      <div className={styles["all-article-container"]}>
        <Spin spinning={loadingMore}>
          {data.dataSource.map((item) => (
            <Article dataSource={item} key={item.id} />
          ))}
        </Spin>
      </div>
      <div className={styles["all-article-footer"]}>
        {isShowLoadMore && (
          <a className={styles["footer-next"]} onClick={loadMore}>
            {loadingMore ? "正在加载中..." : "加載更多"}
            <Spin spinning={loadingMore}></Spin>
          </a>
        )}
        {data.dataSource.length === 0 && <p className={styles["footer-none"]}>車款暫無相關文章</p>}
      </div>
    </div>
  );
};

export default ArticleList;
